<template>
    <div id="app"
         :class="[
             { collapsed: isMenuCollapsed },
             {'isOnline': !user.isOffline},
             { onmobile: isOnMobile }
         ]">

        <Loader :text="isLoggingOut ? 'Loggar ut' : ''" v-show="isLoggingOut || user.isLoading" />

        <!--Offline menu-->
        <b-navbar toggleable
                  type="dark"
                  variant="dark"
                  class="topMenu"
                  v-if="user.isOffline && !user.isLoading && !isLoggingOut">
            <b-navbar-brand>
                <NavbarLogotype :user="user" />
            </b-navbar-brand>
            <b-navbar-nav class="ml-auto">
                <b-button variant="primary"
                          size="sm"
                          :to="{name:'startPage'}">Egenkontroller</b-button>
            </b-navbar-nav>
        </b-navbar>

        <!--Mobile menu-->
        <b-navbar toggleable
                  type="dark"
                  variant="dark"
                  class="topMenu"
                  v-if="isOnMobile && !user.isOffline && !user.isLoading && !isLoggingOut">
            <b-navbar-brand>
                <NavbarLogotype :user="user" />
            </b-navbar-brand>
            <b-navbar-nav class="ml-auto">
                <b-nav-item @click="isMenuCollapsed = false">
                    <font-awesome-icon icon="bars" />
                </b-nav-item>
            </b-navbar-nav>
        </b-navbar>

        <div>
            <router-view class="container-fluid" v-show="!user.isLoading && !isLoggingOut" />

            <!--Sidebar menu-->
            <div v-if="!user.isOffline && !user.isLoading && !isLoggingOut">
                <sidebar-menu :menu="menu"
                              :collapsed="isMenuCollapsed"
                              width="280px"
                              :hide-toggle="false"
                              :show-child="false"
                              :show-one-child="true"
                              @toggle-collapse="onToggleCollapse"
                              @item-click="onItemClick">
                    <div slot="header" />
                    <div slot="footer">
                        <div v-if="user.user" class="text-white text-center">
                            <a @click.prevent="logout"
                               v-b-tooltip.hover
                               href="#"
                               title="Logga ut">
                                <font-awesome-icon icon="sign-out-alt" />
                                <span v-show="!isMenuCollapsed">{{ user.user.fullName }}</span>
                            </a>
                        </div>
                    </div>
                    <span slot="toggle-icon"><font-awesome-icon icon="arrows-alt-h" /></span>
                    <span slot="dropdown-icon"><font-awesome-icon icon="chevron-right" /></span>
                </sidebar-menu>
                <div v-if="isOnMobile && !isMenuCollapsed"
                     class="sidebar-overlay"
                     @click="collapseMenuOnOutsideClick()" />
                <div class="sidebar-menu-mobile--toggle-button-wrapper">
                    <b-button @click="isMenuCollapsed = false">
                        <font-awesome-icon icon="arrows-alt-h" />
                    </b-button>
                </div>
                <CustomerNotesSidebar ref="customerNotesSidebar"
                                      :customer-id="user.selectedCustomer ? user.selectedCustomer.id : null"
                                      :can-read-notes="isSuperAdmin"
                                      @save="onSaveCustomerNotes"
                                      @notes-loaded="onNotesLoaded" />

            </div>
        </div>
    </div>
</template>

<script>
    import { mapActions, mapState } from 'vuex';
    import MenuLogotype from '@/components/MenuLogotype';
    import NavbarLogotype from '@/components/NavbarLogotype';
    import CustomerSelectList from '@/components/customer/CustomerSelectList';
    import { getByKey } from '@/services/setting.service';
    import { fetchStatuses } from '@/services/control-position-status.service';
    import CustomerNotesSidebar from '@/components/customer/CustomerNotesSidebar';
    import { post } from '@/helpers/api';
    import Loader from '@/components/Loader';

    export default {
        name: 'App',
        components: {
            MenuLogotype,
            NavbarLogotype,
            CustomerSelectList,
            CustomerNotesSidebar,
            Loader,
        },
        data() {
            return {
                isMenuCollapsed: window.innerWidth <= 1024,
                isOnMobile: window.innerWidth <= 768,
                isOnTablet: window.innerWidth > 768 && window.innerWidth <= 1024,
                lastWindowWidth: window.innerWidth,
                customerNotes: '',
                isLoggingOut: false,
            };
        },
        computed: {
            ...mapState({
                user: (state) => state.user,
                customerMenu: (state) => state.archive.menu,
                customerAdminMenu: (state) => state.archive.customerAdminMenu,
                adminMenu: (state) => state.archive.adminMenu,
                superAdminMenu: (state) => state.archive.superAdminMenu,
                globalDocs: (state) => state.archive.globalDocs,
            }),

            isSuperAdmin() {
                if (!this.user.isLoading) {
                    return this.user.user && this.user.user.roles.includes('SuperAdmin');
                } else {
                    return false;
                }
            },

            menu() {
                const hasNotes = !!this.customerNotes;

                const notesMenuItem = {
                    title: 'Anteckning',
                    icon: {
                        element: 'font-awesome-icon',
                        class: hasNotes ? 'customer-notes-icon--has-notes' : '',
                        attributes: {
                            icon: hasNotes ? 'comment-exclamation' : 'comment'
                        }
                    }
                };

                const flattenHrefs = (items) => {
                    if (!Array.isArray(items)) {
                        return [];
                    }

                    return items.reduce((acc, item) => {
                        const href = item?.href ?? null;
                        if (typeof href === 'string' && href.length) {
                            acc.push(href);
                        }

                        const child = item?.child ?? null;
                        if (Array.isArray(child) && child.length) {
                            acc.push(...flattenHrefs(child));
                        }

                        return acc;
                    }, []);
                };

                const existingHrefs = new Set(flattenHrefs(this.customerMenu));

                const selectedCustomerId = Number(this.user?.selectedCustomer?.id ?? 0);

                let menuItems = [
                    {
                        component: MenuLogotype,
                        props: {
                            isMenuCollapsed: this.isMenuCollapsed,
                            user: this.user
                        },
                        hiddenOnCollapse: false
                    },
                    {
                        component: CustomerSelectList,
                        hiddenOnCollapse: true
                    },
                    notesMenuItem,
                    ...this.customerMenu,

                    ...this.globalDocs,
                ];

                if (!this.user.isLoading) {
                    if (this.user.user.roles.includes('SuperAdmin')) {
                        menuItems = [...menuItems, ...this.superAdminMenu];
                    } else if (this.user.user.roles.includes('Administrator')) {
                        menuItems = [...menuItems, ...this.adminMenu];
                        menuItems.splice(2, 1); // Remove notesMenu.
                    } else if (this.user.user.roles.includes('CustomerAdmin')) {
                        menuItems = [...menuItems, ...this.customerAdminMenu];
                        menuItems.splice(2, 1); // Remove notesMenu.
                    } else if (this.user.user.roles.includes('ServiceCustomer')) {
                        menuItems.splice(1, 2); // Remove SelectCustomer & notesMenu.
                    } else {
                        menuItems.splice(2, 1); // Remove notesMenu.
                    }
                }
                return menuItems;
            }
        },
        async mounted() {
            await this.getUser();

            const role = this.user.user.roles[0];

            const routeNames = ['manage-building-list', 'manage-building'];

            if (!getByKey('isOffline')) {
                await this.getGlobalDocsMenu();
                await this.getMenu();
                await fetchStatuses();
            }

            if (role === 'ServiceCustomer' && !routeNames.includes(this.$route.name)) {
                this.$router.push({ name: 'manage-building-list' });
                return;
            }
        },
        created() {
            window.addEventListener('resize', this.onResize);
        },
        destroyed() {
            window.removeEventListener('resize', this.onResize);
        },
        methods: {
            ...mapActions('user', ['getUser']),
            ...mapActions('user', ['setCustomer']),
            ...mapActions('user', ['setBuilding']),
            ...mapActions('archive', ['getMenu', 'getGlobalDocsMenu']),
            onToggleCollapse(collapsed) {
                this.isMenuCollapsed = collapsed;
            },
            onItemClick(event, item, node) {
                if (item && item.title === 'Anteckning') {
                    if (event && typeof event.preventDefault === 'function') {
                        event.preventDefault();
                    }

                    if (this.$refs.customerNotesSidebar && this.$refs.customerNotesSidebar.open) {
                        this.$refs.customerNotesSidebar.open();
                    }

                    return;
                }

                if (!item.child && this.isOnMobile)
                    this.isMenuCollapsed = true;
            },
            collapseMenuOnOutsideClick() {
                this.isMenuCollapsed = true;
            },
            onResize() {
                if (!this.lastWindowWidth || window.innerWidth !== this.lastWindowWidth) {
                    this.lastWindowWidth = window.innerWidth;

                    if (window.innerWidth <= 768) {
                        this.isOnTablet = false;
                        this.isOnMobile = true;
                        this.isMenuCollapsed = true;
                    }
                    else if (window.innerWidth <= 1024) {
                        this.isOnTablet = true;
                        this.isOnMobile = false;
                        this.isMenuCollapsed = true;
                    }
                    else {
                        this.isOnTablet = false;
                        this.isOnMobile = false;
                        this.isMenuCollapsed = false;
                    }
                }
            },
            async logout() {
                this.isLoggingOut = true;
                await Promise.all([
                    this.setCustomer(null),
                    this.setBuilding(null)
                ]);

                try {
                    await fetch('/Logout', {
                        method: 'GET',
                        credentials: 'include'
                    });
                } catch (e) {
                    console.warn('Fetch till /Logout misslyckades', e);
                }

                await new Promise(resolve => setTimeout(resolve, 1500));

                window.location.replace('/');
            },
            async onSaveCustomerNotes(notes) {
                const selected = this.user.selectedCustomer;

                if (!selected) {
                    return;
                }

                try {
                    await post('Customer', 'SetNotes', {
                        id: selected.id,
                        notes: notes
                    });

                    this.customerNotes = notes || '';
                } catch (e) {
                    alert('Kunde inte spara anteckningarna. Försök igen.');
                }
            },
            onNotesLoaded(notes) {
                this.customerNotes = notes || '';
            },
        }
    };
</script>
<style lang="scss">
@import 'assets/scss/all.scss';
</style>

<style scoped>
    .sidebar-menu-mobile--toggle-button-wrapper {
        position: fixed;
        bottom: 0;
        left: 0;
        z-index: 100;
    }

        .sidebar-menu-mobile--toggle-button-wrapper > button {
            color: #7f8d9d;
            background-color: #272e37;
            border: 0px;
            border-radius: 0;
        }

    #app {
        padding-top: 0px;
        transition: 0.3s ease;
    }

        #app.isOnline:not(.collapsed) {
            padding-top: 15px;
            padding-left: 290px;
        }

        #app.isOnline:is(.onmobile) {
            padding-top: 0px;
            padding-left: 0px;
        }

        #app:not(.isOnline):is(.collapsed) {
            padding-left: 0px;
        }

        #app.collapsed {
            padding-top: 7px;
            padding-left: 50px;
        }

        #app.onmobile:not(.collapsed) .v-sidebar-menu {
            display: flex;
        }

        #app.onmobile {
            padding-top: 7px;
            padding-left: 0px;
        }

            #app.onmobile .v-sidebar-menu {
                display: none;
            }

    .sidebar-overlay {
        position: fixed;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        background-color: #000;
        opacity: 0.5;
        z-index: 900;
    }

    .topMenu {
        margin: -7px 0 7px 0;
    }
</style>

<style>
    .menu-divider {
        height: 1px;
        background-color: #4a5461;
        margin: 10px 10px;
    }

    .customer-notes-icon--has-notes {
        color: rgb(240, 128, 0) !important;
    }

</style>